/**
 * The DOM attribute `ExperienceWrapper` stamps on `<main>` with the active
 * bbtest variant ids, in registry order, comma-separated (empty on control):
 * `<main data-bb-tests="a04a,a07a">`. It is the one place the rendered page
 * says which variants it is, so a screenshot or a browser test can assert what
 * rendered without reading the URL, and the analytics layer can label its
 * events without a second source of truth.
 */
export const BB_TESTS_ATTRIBUTE = "data-bb-tests";

/**
 * The active variant ids as the page carries them, or "" on control, in a
 * server render, or before `<main>` exists. Read at call time from the DOM
 * rather than from React state so the value is the same for every emitter,
 * whatever order effects run in: the page-view push in `PageView` fires from
 * the layout, above the provider that would otherwise carry the ids.
 *
 * This is a measurement read and nothing else: one attribute lookup on the
 * element the server rendered, no node created, no style or attribute
 * written, no layout touched. It is the analytics layer's counterpart of the
 * SSR marker, the same way the Invoca auto-tag reads the rendered anchors it
 * labels, and it is the only DOM access the bbtest harness makes on the
 * client. Anything that changes what the page shows stays in the server-side
 * tree transforms (lib/utils/studio/applyBbTestVariants.ts).
 */
export function readActiveBbTestsFromDocument(): string {
  if (typeof document === "undefined") return "";
  // tag-conform: measurement-exception -- one querySelector READ of the
  // data-bb-tests attribute the server stamped on <main>, so every GTM push
  // can carry bb_variant. Zero fabrication (no node created), zero style or
  // attribute mutation; the value is only reported. Everything that changes
  // what the page renders stays in the server-side tree transforms.
  const main = document.querySelector(`main[${BB_TESTS_ATTRIBUTE}]`);
  return main?.getAttribute(BB_TESTS_ATTRIBUTE)?.trim() ?? "";
}
